import { createSlice } from "@reduxjs/toolkit";
import { templateApi } from "../../api/templateApi";
import { toast } from "../../components/common/toast";
import type { AppDispatch, RootState } from "../../app/store";
import type {
  Template,
  TemplateSearchPayload,
} from "../../types/template.types";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  uploadMediaFiles,
  createResolvedMedia,
  buildRenamePayload,
  applyRename,
  buildDeletePayload,
  applyDelete,
} from "../../utils/mediaUtils";
import { mediaLinkKey, resolveMediaLinks } from "../../utils/mediaLinkBatch";

// The newest first-page request. A fresh search term must not be dropped
// because an earlier one is still loading, so a first page always runs and an
// answer to anything older than it is discarded.
let latestFirstPage = 0;

export const fetchTemplates = (payload: TemplateSearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().template;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage ? ++latestFirstPage : latestFirstPage;
    const superseded = () => ticket !== latestFirstPage;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        isAppAdmin: userData.isAppAdmin ?? true,
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "modificationdate",
        // Newest-updated first, like every other listing. This asked for
        // ascending, which put the oldest template at the top of the page.
        sortAscending: payload.sortAscending ?? false,
        includeFacets: false,
        groupIds: [`${(await userData).companyId}`],
        ids: payload.ids || [],
        // Admins' "Show Deleted" filter.
        includeDeleted: payload.includeDeleted ?? false,
        contractCategoryIds: Array.isArray(payload.contractCategoryIds)
          ? payload.contractCategoryIds
          : payload.contractCategoryIds
            ? [payload.contractCategoryIds]
            : [],
        isActive: payload.isActive ?? null,
        includeDocument: payload.includeDocument !== false,
        createdBy: (payload.createdBy || []).map((item: any) =>
          typeof item === "object" ? item.id : item,
        ),
        updatedBy: (payload.updatedBy || []).map((item: any) =>
          typeof item === "object" ? item.id : item,
        ),
        currentProfileId: userData.currentLoggedInUserId,
        searchText: payload.searchText || "",
        ...(payload.startDate
          ? { startDate: new Date(payload.startDate).toISOString() }
          : {}),
        ...(payload.endDate
          ? { endDate: new Date(payload.endDate).toISOString() }
          : {}),
      };


      const response = await templateApi.searchTemplate(requestBody);
      if (superseded()) return;
      const fetchedData: Template[] = response.data || response.items || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalTemplates(totalCount));

      // One media request for the page, not one per template.
      const ATTACHMENT_MEDIA_TYPE = 3;
      const links = await resolveMediaLinks(
        fetchedData,
        (item, want) => {
          for (const media of (item.medias || []) as any[]) {
            want(item.groupId, ATTACHMENT_MEDIA_TYPE, media.s3Url);
          }
        },
        templateApi.getMediaLink,
        "Template",
      );
      if (superseded()) return;

      const resolvedData = fetchedData.map((templateItem: Template) => {
        const updatedTemplate = {
          ...templateItem,
          id: templateItem.id,
        };

        if (templateItem.medias && templateItem.medias.length > 0) {
          updatedTemplate.medias = templateItem.medias.map((media: any) => ({
            ...media,
            url:
              links.get(mediaLinkKey(ATTACHMENT_MEDIA_TYPE, media.s3Url)) || "",
          }));
          updatedTemplate.mediaLinks = null;
        }

        return updatedTemplate;
      });

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setTemplates(resolvedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendTemplates(resolvedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }

      return resolvedData;
    } catch (err) {
      console.error("Fetch Templates Error:", err);
      toast.error(
        i18n.t("contractTemplates:sliceMessages.fetchError", {
          defaultValue: "Failed to fetch templates",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const addNewTemplate = (formData: Partial<Template>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const res = await templateApi.createTemplate(formData);
      const newTemplate: Template = {
        id: res?.id || res,
        isActive: true,
        templateName: formData.templateName || "",
        contractCategoryId: formData.contractCategoryId || "",
        contractCategoryName: formData.contractCategoryName || "",
        templateJson: formData.templateJson || "",
        medias: formData.medias || null,
        templatePlaceholders: formData.templatePlaceholders || [],
        creationDate: new Date().toISOString(),
        modificationDate: new Date().toISOString(),
        createdBy: "",
        modifiedBy: "",
        mediaLinks: formData.mediaLinks || null,
        placeholders: formData.placeholders || null,
        ...formData,
      };

      const { items } = getState().template;
      dispatch(setTemplates([newTemplate, ...items]));

      toast.success(
        i18n.t("contractTemplates:sliceMessages.addSuccess", {
          defaultValue: "Template added successfully",
        }),
      );
      return res;
    } catch (err) {
      console.error("Add Template Error:", err);
      toast.error(
        i18n.t("contractTemplates:sliceMessages.addError", {
          defaultValue: "Failed to add template",
        }),
      );
    }
  };
};

export const editTemplate = (formData: Partial<Template>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const apiPayload = { ...formData };

      await templateApi.updateTemplate(apiPayload);

      const { items } = getState().template;
      const updatedList = items.map((item: Template) =>
        item.id === formData.id ? { ...item, ...formData } : item,
      );

      dispatch(setTemplates(updatedList));

      toast.success(
        i18n.t("contractTemplates:sliceMessages.editSuccess", {
          defaultValue: "Template updated successfully",
        }),
      );
    } catch (err) {
      console.error("Update Template Error:", err);
      toast.error(
        i18n.t("contractTemplates:sliceMessages.editError", {
          defaultValue: "Failed to update template details",
        }),
      );
    }
  };
};

export const uploadTemplateImages = (templateId: string, files: File[]) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const state = getState();
      const userId = (await getCurrentUser()).userId;

      const { payloads, links } = await uploadMediaFiles(
        templateApi,
        templateId,
        files,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        templateId,
        userId,
      );

      const updatedItems = state.template.items.map((item: Template) =>
        item.id === templateId
          ? {
              ...item,
              medias: [...(item.medias || []), ...newEntries],
              mediaLinks: null,
            }
          : item,
      );

      dispatch(setTemplates(updatedItems));

      toast.success(
        i18n.t("common:slices.contractTemplates.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} images uploaded successfully`,
        }),
      );
    } catch (err) {
      console.error("Template Upload Error:", err);
      toast.error(
        i18n.t("common:slices.contractTemplates.uploadError", {
          defaultValue: "Failed to upload images",
        }),
      );
      throw err;
    }
  };
};

export const renameTemplateImage = (
  templateId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();
      const currentTemplate = state.template.items.find(
        (t: Template) => t.id === templateId,
      );

      const targetFile = currentTemplate?.medias?.find(
        (m: any) => m.id === fileId,
      );

      if (!targetFile) {
        toast.error(i18n.t("slices.shared.fileNotFound", { ns: "common" }));
        return;
      }

      const payload = buildRenamePayload(targetFile, templateId, newName);

      await templateApi.uploadMedia(templateId, payload);

      const updatedItems = state.template.items.map((item: Template) =>
        item.id === templateId
          ? {
              ...item,
              medias: applyRename(item.medias || [], fileId, newName),
            }
          : item,
      );

      dispatch(setTemplates(updatedItems));

      toast.success(
        i18n.t("common:slices.contractTemplates.renameSuccess", {
          defaultValue: "Image renamed successfully",
        }),
      );
    } catch (err) {
      console.error("Template Media Rename Error:", err);
      toast.error(
        i18n.t("common:slices.contractTemplates.renameError", {
          defaultValue: "Failed to rename image",
        }),
      );
    }
  };
};

export const removeTemplateImage = (
  templateId: string,
  filesToRemove: Array<{ id: string }>,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) return;

      const state = getState();
      const currentTemplate = state.template.items.find(
        (t: Template) => t.id === templateId,
      );

      if (!currentTemplate?.medias) return;

      const idsToRemove = filesToRemove.map((f) => f.id);

      const payload = buildDeletePayload(
        currentTemplate.medias,
        templateId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await templateApi.uploadMedia(templateId, payload);

      const updatedItems = state.template.items.map((item: Template) =>
        item.id === templateId
          ? {
              ...item,
              medias: applyDelete(item.medias || [], idsToRemove),
            }
          : item,
      );

      dispatch(setTemplates(updatedItems));

      toast.success(
        i18n.t("common:slices.contractTemplates.deleteImageSuccess", {
          defaultValue: "Image removed successfully",
        }),
      );
    } catch (err) {
      console.error("Template Remove Image Error:", err);
      toast.error(
        i18n.t("common:slices.contractTemplates.deleteImageError", {
          defaultValue: "Failed to remove image",
        }),
      );
    }
  };
};

export const removeTemplate = (id: string, reason: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await templateApi.removeTemplate(id, reason);

      const { items } = getState().template;
      const filteredList = items.filter((item: Template) => item.id !== id);

      dispatch(setTemplates(filteredList));

      toast.success(
        i18n.t("contractTemplates:sliceMessages.removeSuccess", {
          defaultValue: "Template removed successfully",
        }),
      );
    } catch (err) {
      console.error("Error deleting template:", err);
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("contractTemplates:sliceMessages.removeError", {
            defaultValue: "Failed to remove template",
          }),
        ),
      );
      throw err;
    }
  };
};

export const handleActivateTemplate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await templateApi.activateTemplate(id);

      const { items } = getState().template;

      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: true } : item,
      );

      dispatch(setTemplates(updatedList));

      toast.success(
        i18n.t("contractTemplates:sliceMessages.activateSuccess", {
          defaultValue: "Template status updated successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("contractTemplates:sliceMessages.activateError", {
          defaultValue: "Failed to update template status",
        }),
      );
    }
  };
};

export const handleDeactivateTemplate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await templateApi.deActivateTemplate(id);

      const { items } = getState().template;

      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: false } : item,
      );

      dispatch(setTemplates(updatedList));

      toast.success(
        i18n.t("contractTemplates:sliceMessages.deactivateSuccess", {
          defaultValue: "Template status updated successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("contractTemplates:sliceMessages.deactivateError", {
          defaultValue: "Failed to update template status",
        }),
      );
    }
  };
};

interface TemplateState {
  items: Template[];
  isLoading: boolean;
  pageIndex: number;
  hasMore: boolean;
  totalTemplates: number;
}

const initialState: TemplateState = {
  items: [],
  isLoading: false,
  pageIndex: 0,
  hasMore: true,
  totalTemplates: 0,
};

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {
    setTemplates: (state, action) => {
      state.items = action.payload;
    },
    appendTemplates: (state, action) => {
      state.items = [...state.items, ...action.payload];
    },
    setPageIndex: (state, action) => {
      state.pageIndex = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setTotalTemplates: (state, action) => {
      state.totalTemplates = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    resetTemplateList: (state) => {
      state.items = [];
      state.pageIndex = 0;
      state.hasMore = true;
    },
  },
});

export const {
  setTemplates,
  appendTemplates,
  setPageIndex,
  setHasMore,
  resetTemplateList,
  setLoading,
  setTotalTemplates,
} = templateSlice.actions;

export default templateSlice.reducer;
